import { Block, DoneOutline, NumbersOutlined, PercentOutlined, SettingsSuggestOutlined } from '@mui/icons-material'
import { Box, FormControl, InputAdornment, Stack } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import GridToolbar from '@/components/dataDisplay/GridToolbar'
import CustomButton from '@/components/inputs/CustomButton'
import CustomIconButton from '@/components/inputs/CustomIconButton'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import FloatTextField from '@/components/inputs/FloatTextField'
import IntegerTextField from '@/components/inputs/IntegerTextField'
import type { BidType } from '@/features/bidding/constants'
import { MAXIMUM_MW_PER_PTU } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { BulkFillMode } from '@/features/bidding/types/bid'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import getBidConfig from '@/features/bidding/utils/getBidConfig'
import { getBidDisplayUnit } from '@/features/bidding/utils/getBidDisplayUnit'

/**
 * Module augmentation is necessary to use slotProps without TypeScript errors.
 */
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends OfferedBidPtusDataGridToolbarProps {}
}

type OfferedBidPtusDataGridToolbarProps = {
  acceptsOnlyPositiveInteger: boolean
  bidType: BidType
  deliveryDay: MarketDate
  shouldHaveOptionForRatioOfAcceptedCapacity: boolean
  shouldHaveOptionForRatioOfOfferedCapacity: boolean
  shouldHaveOptionForRatioOfSuggested: boolean
  saveDraftCurrentValues: () => void
  onFillAll: (bulkMode: BulkFillMode, value: number | undefined) => void
}

const OfferedBidPtusDataGridToolbar = ({
  acceptsOnlyPositiveInteger = false,
  bidType,
  deliveryDay,
  saveDraftCurrentValues,
  shouldHaveOptionForRatioOfAcceptedCapacity,
  shouldHaveOptionForRatioOfOfferedCapacity,
  shouldHaveOptionForRatioOfSuggested,
  onFillAll,
}: Readonly<OfferedBidPtusDataGridToolbarProps>) => {
  const { t, i18n } = useTranslation()
  const { selectedCountry } = useBiddingContext()

  const [bulkFillMode, setBulkFillMode] = useState(BulkFillMode.FORECAST_RATIO)
  const [bulkFillValue, setBulkFillValue] = useState<number | undefined>()

  const unit = getBidDisplayUnit(getBidConfig(selectedCountry, bidType).volumeUnit)

  const actions = (
    <>
      <SaveDraftCurrentValuesButton saveDraftCurrentValues={saveDraftCurrentValues} />

      <OfferNoVolumeButton onFillAll={onFillAll} />

      <Stack sx={{ flexDirection: 'row', gap: 1 }}>
        <BulkFillTypeSelect
          bulkFillMode={bulkFillMode}
          setBulkFillMode={setBulkFillMode}
          shouldHaveOptionForRatioOfAcceptedCapacity={shouldHaveOptionForRatioOfAcceptedCapacity}
          shouldHaveOptionForRatioOfOfferedCapacity={shouldHaveOptionForRatioOfOfferedCapacity}
          shouldHaveOptionForRatioOfSuggested={shouldHaveOptionForRatioOfSuggested}
        />
        <FormControl>
          <BulkFillInputField
            key={bulkFillMode}
            acceptsOnlyPositiveInteger={acceptsOnlyPositiveInteger && bulkFillMode == BulkFillMode.ABSOLUTE_VALUE}
            bulkFillMode={bulkFillMode}
            setBulkFillValue={setBulkFillValue}
            unit={unit}
          />
        </FormControl>
      </Stack>

      <CustomButton
        disabled={bulkFillValue == undefined}
        variant="contained"
        onClick={() => onFillAll(bulkFillMode, bulkFillValue)}
      >
        {t('bidding.create_bid.fill_all')}
      </CustomButton>
    </>
  )

  return (
    <GridToolbar
      actions={actions}
      filters={
        <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
          <DatePicker
            disabled
            defaultValue={deliveryDay.getStartOfDay()}
            enableAccessibleFieldDOMStructure={false}
            format="DD"
            label={t('bidding.create_bid.bid_date')}
            sx={{ width: 180 }}
          />
        </LocalizationProvider>
      }
    />
  )
}

const SaveDraftCurrentValuesButton = ({ saveDraftCurrentValues }) => {
  const { t } = useTranslation()

  return (
    <CustomIconButton
      Icon={DoneOutline}
      aria-label={t('bidding.create_bid.save_draft_current_values_button_label')}
      data-testid="save-draft-current-values-button"
      iconProps={{ titleAccess: t('bidding.create_bid.save_draft_current_values_button_label') }}
      onClick={() => saveDraftCurrentValues()}
    />
  )
}

const OfferNoVolumeButton = ({ onFillAll }) => {
  const { t } = useTranslation()

  return (
    <CustomIconButton
      Icon={Block}
      aria-label={t('bidding.create_bid.offer_no_volume_button_label')}
      data-testid="offer-no-capacity-button"
      iconProps={{ titleAccess: t('bidding.create_bid.offer_no_volume_button_label') }}
      onClick={() => onFillAll(BulkFillMode.ABSOLUTE_VALUE, 0)}
    />
  )
}

const BulkFillTypeSelect = ({
  bulkFillMode,
  setBulkFillMode,
  shouldHaveOptionForRatioOfAcceptedCapacity,
  shouldHaveOptionForRatioOfOfferedCapacity,
  shouldHaveOptionForRatioOfSuggested,
}) => {
  const { t } = useTranslation()

  const options = [
    {
      id: BulkFillMode.FORECAST_RATIO,
      value: BulkFillMode.FORECAST_RATIO,
      label: t('bidding.create_bid.forecasted_ratio'),
      icon: <SettingsSuggestOutlined />,
    },
    ...(shouldHaveOptionForRatioOfSuggested
      ? [
          {
            id: BulkFillMode.SUGGESTED_RATIO,
            value: BulkFillMode.SUGGESTED_RATIO,
            label: t('bidding.create_bid.suggested_ratio'),
            icon: <PercentOutlined />,
          },
        ]
      : []),
    ...(shouldHaveOptionForRatioOfOfferedCapacity
      ? [
          {
            id: BulkFillMode.OFFERED_CAPACITY_RATIO,
            value: BulkFillMode.OFFERED_CAPACITY_RATIO,
            label: t('bidding.create_bid.offered_capacity_ratio'),
            icon: <PercentOutlined />,
          },
        ]
      : []),
    ...(shouldHaveOptionForRatioOfAcceptedCapacity
      ? [
          {
            id: BulkFillMode.ACCEPTED_CAPACITY_RATIO,
            value: BulkFillMode.ACCEPTED_CAPACITY_RATIO,
            label: t('bidding.create_bid.accepted_capacity_ratio'),
            icon: <PercentOutlined />,
          },
        ]
      : []),
    {
      id: BulkFillMode.ABSOLUTE_VALUE,
      value: BulkFillMode.ABSOLUTE_VALUE,
      label: t('bidding.create_bid.absolute_value'),
      icon: <NumbersOutlined />,
    },
  ]

  return (
    <CustomSelectField
      data-testid="bulk-fill-type"
      id="bulk-fill"
      label={t('bidding.create_bid.bulk_fill')}
      options={options}
      value={bulkFillMode}
      onChange={(event) => setBulkFillMode(event.target.value)}
    />
  )
}

const INPUTS_WITH_PERCENTAGE = new Set([
  BulkFillMode.FORECAST_RATIO,
  BulkFillMode.SUGGESTED_RATIO,
  BulkFillMode.OFFERED_CAPACITY_RATIO,
  BulkFillMode.ACCEPTED_CAPACITY_RATIO,
])

const INPUTS_DEFAULTING_TO_100 = new Set([BulkFillMode.OFFERED_CAPACITY_RATIO, BulkFillMode.ACCEPTED_CAPACITY_RATIO])

const BulkFillInputField = ({ acceptsOnlyPositiveInteger, bulkFillMode, setBulkFillValue, unit }) => {
  const inputAdornment = INPUTS_WITH_PERCENTAGE.has(bulkFillMode) ? '%' : unit
  const initialValue = INPUTS_DEFAULTING_TO_100.has(bulkFillMode) ? '100' : undefined

  if (acceptsOnlyPositiveInteger) {
    return (
      <Box width={120}>
        <IntegerTextField
          initialValue={initialValue}
          max={MAXIMUM_MW_PER_PTU}
          min={0}
          textFieldProps={{
            InputProps: {
              endAdornment: <InputAdornment position="end">{inputAdornment}</InputAdornment>,
            },
            inputProps: { 'data-testid': 'bulk-fill-value' },
            label: 'Value',
          }}
          onValueChange={setBulkFillValue}
        />
      </Box>
    )
  }

  return (
    <Box width={120}>
      <FloatTextField
        initialValue={initialValue}
        max={MAXIMUM_MW_PER_PTU}
        min={0}
        textFieldProps={{
          InputProps: {
            endAdornment: <InputAdornment position="end">{inputAdornment}</InputAdornment>,
          },
          inputProps: { 'data-testid': 'bulk-fill-value' },
          label: 'Value',
        }}
        onValueChange={setBulkFillValue}
      />
    </Box>
  )
}

export default OfferedBidPtusDataGridToolbar
